import {
    EventId,
    MassAssignStrategy,
    PositionsSlotId,
    TeamCode,
    TeamRightPanelQuery,
    UserInfoTeamRightPanelFragment,
    UsersInfoId
} from 'common/src/generated/types';
import { Emptyable } from 'common/src/util/emptyable';
import { pick, sortBy, uniq, uniqBy } from 'lodash-es';
import * as React from 'react';
import {
    executeTeamRightPanelMemberQuery,
    useTeamRightPanelQuery,
    useVolunteersRegistrationsMassAssignMutation
} from '../../generated/graphqlHooks';
import { getToken } from '../../util/aws/cognito';

export type TeamRightPanelSlot = NonNullable<
    TeamRightPanelQuery['event']['team']
>['members'][0]['positionsSlots'][number];

interface ITeamRightPanelContext {
    allSlots: TeamRightPanelSlot[];
    form: Emptyable<NonNullable<TeamRightPanelQuery['event']['team']>['form']>;
    loader: React.JSX.Element | null;
    memberIdToAssignedSlotsIds: Record<number, PositionsSlotId[]>;
    memberIdToWishedSlotsIds: Record<number, PositionsSlotId[]>;
    members: UserInfoTeamRightPanelFragment[];

    assign(userInfoId: UsersInfoId, positionSlotId: PositionsSlotId): void;
}

const TeamRightPanelContext = React.createContext<ITeamRightPanelContext>({} as any);

interface ITeamRightPanelContextProviderProps {
    children: React.ReactNode;
    eventId: EventId;
    teamCode: TeamCode;
}

export const TeamRightPanelContextProvider = (props: ITeamRightPanelContextProviderProps) => {
    const { data, loader } = useTeamRightPanelQuery({
        eventId: props.eventId,
        teamCode: props.teamCode
    });
    const { mutate: massAssign } = useVolunteersRegistrationsMassAssignMutation();
    const [members, setMembers] = React.useState<UserInfoTeamRightPanelFragment[]>([]);
    const allWishedSlots = React.useMemo(() => {
        const wishedCategoriesSlots = members.flatMap((member) =>
            member.positionsCategories.flatMap((category) =>
                category.positions.flatMap((position) =>
                    position.slots.map((slot) => ({
                        ...slot,
                        position: pick(position, ['id', 'name', 'icon', 'color'])
                    }))
                )
            )
        );
        const wishedPositionsSlots = members.flatMap((member) =>
            member.positions.flatMap((position) =>
                position.slots.map((slot) => ({
                    ...slot,
                    position: pick(position, ['id', 'name', 'icon', 'color'])
                }))
            )
        );
        const wishedSlots = members.flatMap((member) => member.positionsSlots);

        return uniqBy(
            wishedCategoriesSlots.concat(wishedPositionsSlots).concat(wishedSlots),
            (s) => s.id
        );
    }, [members]);
    const allAssignedSlots = React.useMemo(
        () =>
            uniqBy(
                members.flatMap((member) =>
                    member.positionsSlotsUsersInfos.map((psui) => ({
                        ...psui.positionSlot,
                        position: psui.position
                    }))
                ),
                (s) => s.id
            ),
        [members]
    );
    const allSlots = React.useMemo(
        () =>
            sortBy(
                uniqBy(allWishedSlots.concat(allAssignedSlots), (s) => s.id),
                (s) => [s.position.name.toLowerCase(), s.range.start!.toMillis()]
            ),
        [allWishedSlots, allAssignedSlots]
    );
    const memberIdToWishedSlotsIds = React.useMemo(
        () =>
            Object.fromEntries(
                members.map((member) => {
                    const wishedSlotsIds = uniq(
                        member.positionsCategories
                            .flatMap((c) => c.positions.flatMap((p) => p.slots.map((s) => s.id)))
                            .concat(member.positions.flatMap((p) => p.slots.map((s) => s.id)))
                            .concat(member.positionsSlots.map((s) => s.id))
                    );

                    return [member.userInfo.id, wishedSlotsIds];
                })
            ),
        [members]
    );
    const memberIdToAssignedSlotsIds = React.useMemo(
        () =>
            Object.fromEntries(
                members.map((member) => {
                    const assignedSlotsIds = member.positionsSlotsUsersInfos.map(
                        (psui) => psui.positionSlot.id
                    );

                    return [member.userInfo.id, assignedSlotsIds];
                })
            ),
        [members]
    );
    const assign = React.useCallback(
        async (userInfoId: UsersInfoId, positionSlotId: PositionsSlotId) => {
            await massAssign({
                eventId: props.eventId,
                massAssign: {
                    positionsSlotsIds: [positionSlotId],
                    selecteds: { ids: [userInfoId] },
                    strategy: MassAssignStrategy.Add
                }
            });

            const { event } = await executeTeamRightPanelMemberQuery(
                { eventId: props.eventId, userInfoId },
                await getToken()
            );

            setMembers((currentMembers) =>
                currentMembers.map((m) =>
                    m.userInfo.id === userInfoId ? event.volunteerRegistration : m
                )
            );
        },
        [props.eventId, setMembers]
    );

    React.useEffect(() => {
        setMembers(data.event?.team?.members ?? []);
    }, [data.event, setMembers]);

    return (
        <TeamRightPanelContext.Provider
            value={{
                allSlots,
                form: data.event?.team?.form,
                loader,
                memberIdToAssignedSlotsIds,
                memberIdToWishedSlotsIds,
                members,
                assign
            }}
        >
            {props.children}
        </TeamRightPanelContext.Provider>
    );
};

export function useTeamRightPanelContext() {
    return React.useContext(TeamRightPanelContext);
}
